<i18n>
{
  "en": {
    "title": "Access to Energy Sustainability Report 2017-2018",
    "description": "The Access to Energy program actively involves local stakeholders to solve the energy gap by bringing safe, clean, green, sustainable electricity to communities all over the world. Read about the program’s 2017-2018 highlights and learn about its offers, business models, impact investments, and training.",
    "outreach": {
      "section": "Outreach and Initiatives",
      "title": "Access to Energy",
      "energy": {
        "txt1": {
          "p1": "In 2017, we expanded our range of products, solutions, and services for communities that lack access to energy — from individual lighting to community services and collective electrification. We’re committed to providing innovative solutions that ensure Life Is On for everyone, everywhere, at every moment. This is our promise and our mission.",
          "p2": "The Access to Energy program actively involves local stakeholders, including residents and customers, to bring safe, clean, sustainable electricity to communities all over the world. The Access to Energy program operates in three ways:"
        },
        "table1": {
          "title": "Offers and business models",
          "p1": "To address local electricity access shortages, we design electrical distribution offers via renewable off-grid products and solutions. We deploy these commercially as widely as possible through adapted business models.",
          "p2": "This includes our Mobiya<sup>&trade;</sup>, Homaya<sup>&trade;</sup>, and Villaya<sup>&trade;</sup> products."
        },
        "txt2": {
          "title": "Impact investing",
          "p1": "To address local funding shortages, we invest in innovative, local access-to-energy ventures and subject matter experts through impact investing funds.",
          "p2": "This includes the Energy Access Fund and Schneider Electric Energy Access Fund."
        },
        "txt3": {
          "title": "Training and entrepreneurship",
          "p1": "To address local skill shortages, we support the development of training programs in the energy field.",
          "p2": "With the support of the Schneider Electric Foundation, we partner with local and global not-for-profit education organizations to train disadvantaged people and support informal entrepreneurs in the energy field. We have more than 50 years of experience developing tailored training solutions for professionals and the education sector."
        },
        "txtMedia1": {
          "sectionTitle": "2017 Access to Energy highlights: offers and business models",
          "title": "Bangladesh",
          "p1": "We brought solar-powered portable lamps to a remote island village, where 100 households now have electricity for basic needs."
        },
        "txtMedia2": {
          "title": "Cambodia",
          "p1": "Installed in Bavet city of the Svay Rieng province, a 10 kW solar power plant from Schneider Electric now provides safe, reliable, and modern energy for the surrounding villages."
        },
        "txtMedia3": {
          "title": "Cameroon",
          "p1": "The microfinancing group Pamiga partnered with us to bring more than 3,200 households access to energy with our Mobiya and Homaya solar solutions."
        },
        "txt4": {
          "title": "Congo",
          "p1": "Proton and Schneider Electric provided solar solutions to remote villages in the Democratic Republic of the Congo."
        },
        "vid1": {
          "title": "Access to Energy in Congo",
          "desc": "Schneider and Proton provide solar solutions"
        },
        "txt5": {
          "title": "India",
          "p1": "Schneider Electric and the Schneider Electric India Foundation led two rural electrification projects in Arunachal Pradesh, providing energy access to 375 households with our Mobiya solar lanterns."
        },
        "txt6": {
          "title": "Kenya",
          "p1": "Our new Villaya Microgrid solution is designed, manufactured, and tested in Nairobi. It provides access to energy and socio-economic development to homes, health clinics, schools, and businesses."
        },
        "vid2": {
          "title": "Villaya Microgrids",
          "desc": "Energizing communities with sustainable solutions"
        },
        "txt7": {
          "title": "Myanmar",
          "p1": "We worked with our local partners, Mandalay Yoma, Talent and Technology, and Techno-Hill Engineering, to build microgrids that bring energy to more than 1,000 households across five villages."
        },
        "txt8": {
          "title": "Nepal",
          "p1": "Saral Urja partnered with us to provide electricity to a remote, off-grid village using a solar powered microgrid."
        },
        "barometer1": {
          "title": "2015 – 2017 Planet <span class='t-nowrap'>& Society</span> barometer achievement",
          "cta": "See the full results",
          "card1": {
            "title": "Climate & Development (Sustainability offers)",
            "stat": "x2.21",
            "body": "increase in turnover of Access to Energy program"
          }
        },
        "txt9": {
          "title": "Sustainable, multi-energy solutions in Africa",
          "p1": "Along with the West African Economic and Monetary Union and the African Biofuel and Renewable Energy Company, Schneider Electric is developing a “multi-energy” plant for irrigation, fish farming, and farming transformation.",
          "p2": "This solution is called Villaya Agri-Business.",
          "p3": "Villaya Agri-Business is an innovative solution enabling energy to be captured through a solar thermal and photovoltaic power plant, and to be reused to produce electricity and heat simultaneously. It can supply micro-industries in rural areas, especially food production, processing and storage plants. This multi-energy plant can also store energy in batteries with a 10-year lifespan.",
          "p4": "The project will be implemented over a two-year period, involving feasibility studies, equipment installation, and user training. It will progressively offer up to 100,000 people access to electricity for irrigation, lighting, fish farming, farming transformation, and the provision of drinking water without CO<sub>2</sub> emissions.",
          "p5": "Further, in Kigbe, Nigeria, Schneider Electric and Havenhill Synergy collaborated to build a mini-grid that provides continuous electricity to rural homes and businesses.",
          "p6": "In Adamawa, we partnered with Blue Camel Energy to install six units of Conext<sup>&trade;</sup> XW+ 8548 inverters and 144 units of solar panels to bring power and productivity to a local farm that had previously suffered from frequent outages. Now, more products are available to feed a population of about 450,000 people.",
          "p7": "Finally, Schneider Electric and GVE partnered to provide solar solutions to communities in remote areas, supporting schools helping to reduce cases of malaria."
        },
        "vid3": {
          "title": "Access to Energy in West Africa",
          "desc": "Bringing safe and reliable electricity to local communities"
        },
        "txtMedia4": {
          "sectionTitle": "2017 Access to Energy highlights: impact investing",
          "title": "EAV Fund 1 increased to $90 million",
          "p1": "In 2017, Energy Access Ventures (EAV) increased its Fund 1 size to $90 million and boosted economic growth in sub-Saharan Africa. This was made possible through three investments: The Netherlands company Financierings-Maatschappij voor Ontwikkelingslanden joined the fund whilst existing limited partners Schneider Electric and CDC both increased their commitments.",
          "p2": "EAV invests in companies worldwide to support innovative electricity technologies and business models."
        },
        "quote1": {
          "text": "At FMO we are excited to participate in EAV Fund I. We believe EAV represents a unique opportunity to invest in an existing operational team with a thorough understanding of the energy market in sub-Saharan Africa combined with solid expertise in early-stage investing. This investment fits with FMO’s strategic agenda in terms of promoting renewable energy, inclusive growth, and innovation.",
          "author": "Marieke Roestenberg",
          "title": "Senior Investment Officer, Private Equity, <br>FMO"
        },
        "txtMedia5": {
          "title": "Schneider Electric Energy Access Fund",
          "p1": "The Schneider Electric Energy Access (SEEA) fund brings together Schneider Electric’s employees and business partners around the world to play an active role in bringing access to energy to communities in need. This social welfare investment structure, designed by Schneider Electric to promote responsible development, constitutes a response to new French legislation on employee savings plans.",
          "p2": "At the end of 2017, 5,300 employees in France showed their interest in the Access to Energy program by investing €16.8 million.",
          "p3": "On December 31, 2017, the following amounts were managed by SEEA:",
          "li1": "€3,000,000 in capital invested by Schneider Electric",
          "li2": "€2,300,000 invested by Schneider Énergie Sicav Solidaire (including €500,000 in capital), a mutual fund managing the employee savings scheme for Schneider Electric employees in France",
          "li3": "€200,000 in capital invested by Phitrust Partenaires"
        },
        "quote2": {
          "text": "At Amped Innovation, we partner with social enterprises to design radically affordable products that address basic human needs. We focus on reducing product cost to enable rapid adoption of proven technologies.",
          "author": "Andi Kleissner",
          "title": "Co-founder and COO,",
          "company": "Amped Innovation"
        },
        "txt11": {
          "h2": "2017 Access to Energy highlights: training and entrepreneurship",
          "title": "Vietnam",
          "p1": "In partnership with Green ID and local Women Unions, Schneider Electric trained and supported women from remote communities to bring energy to low-income people. These women entrepreneurs increased their income by 30% and have gained an important social status within their communities.",
          "p2": "The Schneider Electric training lab in Ho Chi Minh City trained 650 underprivileged young people in residential and industrial electricity, and 100% of them got a job right after graduation. In 2018, we hope to train 1,000 – 1,500 students in this center."
        },
        "txtMedia6": {
          "title": "India",
          "p1": "We opened the seventh Solar Energy Training Center in India at the Sri Sri University in Cuttack. We also set up a new Center of Excellence at the New Horizon College of Engineering and extended our collaboration with Kalinga Institute of Technology and C.V. Raman College of Engineering in Bhubaneswar to address the digital skill gap in fast-evolving sectors.",
          "p2": "In October 2017, we inaugurated the Pradhan Mantri Kaushal Kendra Center in Delhi. It’s the first center in the country focusing exclusively on the skill requirements which are aligned to the government’s smart cities initiative.",
          "p3": "Finally, the Schneider Electric India Foundation and Sri Sri Rural Development program began a new basic electrician training center in Central Jail, Srinagar. The training will help prisoners start their new lives with valuable electrical skills. 26 prisoners are undergoing training in the initial classes."
        },
        "quote3": {
          "text": "training and entrepreneurship QUOTE TBD",
          "author": "Author",
          "title": "Title",
          "company": "Company"
        },
        "txt12": {
          "title": "Pakistan",
          "p1": "With the support of Schneider Electric Foundation, two electrical labs were opened in Lahore, two labs in Gujranwala, and one was opened in Wazirabad. Ultimately, 52 labs are planned by the end of 2018.",
          "p2": "201 students have started training, and registration is expected to climb to almost 6,000 with the completion of all labs.",
          "p3": "The labs are being established in partnership with the Technical Education &amp; Vocational Training Authority, the Punjab Vocational Training Council, and the Muslim Hands organization."
        },
        "txt13": {
          "title": "Cambodia",
          "p1": "We partnered with the Don Bosco Foundation to open the Don Bosco Training Center, which will train more than 300 underprivileged youths every year."
        },
        "txt14": {
          "title": "Chad",
          "p1": "In partnership with the Office National pour la Promotion de l’Emploi and the Pan African Institute for Development, 77 out-of-school youths were trained and joined the local job market."
        },
        "txt15": {
          "title": "Columbia",
          "p1": "In partnership with the Colombian Ministry of Labor and the Colombian Ministry of National Education, Schneider Electric held seminars throughout the year, training 79 students and 71 trainers."
        },
        "txt16": {
          "title": "Ivory Coast",
          "p1": "In partnership with our local distributor, LCS Technodidac, we held an educational event gathering professionals from the public and private sectors to address the challenges of developing technical training programs and responding to market needs."
        },
        "txt17": {
          "title": "Algeria",
          "p1": "In partnership with the Algerian Ministry for Vocational Training and Education and the French Ministry for National Education and Research, Schneider Electric took part in the creation of an Algerian-French center, which is dedicated to vocational training in energy and electricity trades. Located in Rouiba, this center specializes in energy efficiency and industrial maintenance."
        },
        "txt18": {
          "title": "Senegal",
          "p1": "In partnership with our local distributor, LCS Technodidac, we held an educational event to address the challenges of developing technical training programs and responding to market needs."
        },
        "txt19": {
          "title": "South Africa",
          "p1": "The Legacy Class of the French South African Schneider Electric Education Center welcomed as many girls as boys. For one year, the students will be trained in electrical engineering on domestic and industrial installations."
        },
        "txt20": {
          "title": "Tanzania",
          "p1": "We installed a 30 kW solar plant and laboratory at the training center and held conferences on the microgrid market model and the IoT's impact in industrial and photovoltaic applications."
        },
        "txt21": {
          "title": "Togo",
          "p1": "After eight months of training at the Energy Generation Academy in Lomé, Togo — a Schneider-supported program offering courses on entrepreneurship in energy — students presented their projects at the first Africa Energy Generation Prize contest."
        },
        "statement1": {
          "title": "We're helping to supply electricity for 100,000 people to power irrigation, lighting, fish farming, and more."
        }
      }
    }
  },
  "fr": {
    "title": "Access to Energy Sustainability Report 2017-2018",
    "description": "The Access to Energy program actively involves local stakeholders to solve the energy gap by bringing safe, clean, green, sustainable electricity to communities all over the world. Read about the program’s 2017-2018 highlights and learn about its offers, business models, impact investments, and training.",
    "outreach": {
      "section": "Initiatives et communautés",
      "title": "Accès à l’énergie",
      "energy": {
        "txt1": {
          "p1": "En 2017, nous avons élargi notre gamme de produits, de solutions et de services pensés pour les populations privées d’accès à l’énergie, allant de l’éclairage individuel à l’électrification collective en passant par les services communautaires. Nous nous engageons à fournir des solutions innovantes afin que la vie s’illumine pour tout le monde, partout et à tout moment. C’est à la fois notre promesse et notre vision. ",
          "p2": "Le programme Accès à l’énergie prévoit une implication active des parties prenantes à l'échelle locale, notamment des résidents et des clients, afin que nous puissions apporter – de manière durable – une énergie propre et sûre aux collectivités. Ce programme s'articule autour de trois axes :"
        },
        "table1": {
          "title": "Offres et modèles économiques",
          "p1": "Pour remédier au manque d’accès à l’électricité à l’échelle locale, nous concevons des systèmes de distribution d’électricité adaptés, via des solutions et produits off-grid renouvelables tels que Mobiya, Homaya et Villaya. Nous déployons et commercialisons ces systèmes de manière aussi large que possible grâce à des modèles économiques adaptés.",
          "p2": "&nbsp;"
        },
        "txt2": {
          "title": "Investissements à impact",
          "p1": "Pour remédier à l'absence de financement à l’échelle locale, nous investissons dans des entreprises locales innovantes sur les questions d’accès à l'énergie et soutenons des experts en la matière via deux fonds d’investissement à impact, Energy Access Ventures et Schneider Electric Energy Access.",
          "p2": "&nbsp;"
        },
        "txt3": {
          "title": "Formation et entrepreneuriat",
          "p1": "Pour remédier aux pénuries de compétences locales, nous appuyons la création de parcours de formation en négoce d'électricité. ",
          "p2": "Avec le soutien de la Fondation Schneider Electric, nous nous associons à des organisations locales et internationales, à but non lucratif et à visée éducative, afin de former des personnes défavorisées dans le domaine de l’énergie."
        },
        "txtMedia1": {
          "sectionTitle": "Faits marquants 2017 : Formation et Entrepreneuriat",
          "title": "Bangladesh",
          "p1": "Nous avons fourni des lampes portables à alimentation solaire à 100 foyers d'un village insulaire isolé : ses habitants ont désormais accès à l’électricité pour satisfaire leurs besoins de base."
        },
        "txtMedia2": {
          "title": "Cambodge",
          "p1": "Dans la province de Svay Rieng, la ville de Bavet abrite une centrale solaire de 10 kW installée par Schneider Electric, qui alimente les villages environnants en énergie sûre, fiable et moderne. "
        },
        "txtMedia3": {
          "title": "Cameroun",
          "p1": "Le groupe de microfinance Pamiga s’est associé à nous pour fournir nos solutions solaires Mobiya et Homaya à plus de 3 200 foyers, leur donnant ainsi accès à l’énergie. "
        },
        "txt4": {
          "title": "Congo",
          "p1": "Proton et Schneider Electric ont fourni des solutions solaires à des villages reculés de la République démocratique du Congo."
        },
        "vid1": {
          "title": "Accès à l’énergie au Congo",
          "desc": "Schneider Electric et Proton ont fourni des solutions solaires"
        },
        "txt5": {
          "title": "Inde",
          "p1": "Schneider Electric et la Fondation Schneider Electric Inde ont mené deux projets d'électrification rurale dans l’Arunachal Pradesh, où nos lampes solaires Mobiya ont donné accès à l’énergie à 375 foyers. "
        },
        "txt6": {
          "title": "Kenya",
          "p1": "Notre nouvelle solution microgrid Villaya est conçue, produite et testée à Nairobi. Grâce à elle, habitations, centres sanitaires, écoles et entreprises ont accès à l’énergie et au développement socio-économique."
        },
        "vid2": {
          "title": "Migrogrids Villaya",
          "desc": "Dynamiser les communautés avec des solutions durables"
        },
        "txt7": {
          "title": "Myanmar",
          "p1": "Nous avons travaillé avec nos partenaires locaux, Mandalay Yoma, Talent and Technology et Techno Hill Engineering, pour construire des microgrids qui alimentent en énergie plus de 1 000 foyers répartis dans 5 villages. "
        },
        "txt8": {
          "title": "Népal",
          "p1": "Saral Urja a conclu un partenariat avec nous afin de fournir de l’électricité à un village isolé et non relié au réseau grâce à un microgrid à énergie solaire."
        },
        "barometer1": {
          "title": "Résultat du baromètre Planète & Société 2015-2017",
          "cta": "Voir l’ensemble des résultats",
          "card1": {
            "title": "Climat et Développement (offres durables)",
            "stat": "x2,21",
            "body": "sur le chiffre d'affaires du programme Accès à l’énergie pour promouvoir le développement"
          }
        },
        "txt9": {
          "title": "Solutions multi-énergies durables en Afrique",
          "p1": "En collaboration avec l’Union économique et monétaire ouest-africaine et la Société africaine des biocarburants et des énergies renouvelables, Schneider Electric développe une centrale « multi-énergies » pour des usages tels que l’irrigation, la pisciculture et la transformation agricole.",
          "p2": "Cette solution innovante est baptisée Villaya Agri-Business.",
          "p3": "Il s'agit d'une centrale solaire composée de panneaux photovoltaïques et de panneaux thermiques, permettant de stocker l’énergie et de la réutiliser pour produire électricité et chaleur simultanément. Elle peut alimenter des micro-industries dans des zones rurales, notamment des entités de production et de transformation alimentaire et des usines de stockage. Cette centrale « multi-énergies » permet également de stocker l'énergie sous forme de batteries pour une durée de vie de dix ans.",
          "p4": "Le projet sera mis en œuvre sur une période de deux ans qui comprend les études de faisabilité, l’installation des équipements et la formation des utilisateurs. Elle fera progressivement accéder près de 100 000 personnes à l’énergie électrique nécessaire pour l’irrigation, l’éclairage, la pisciculture, la transformation agricole et la fourniture d’eau potable, et cela sans émettre de CO<sub>2</sub>.",
          "p5": "En outre, à Kigbe, au Nigéria, Schneider Electric et Havenhill Synergy ont collaboré pour construire un mini-grid alimentant en électricité continue des foyers et entreprises en zone rurale.",
          "p6": "À Adamawa, nous nous sommes associés à Blue Camel Energy pour installer 6 onduleurs Conext XW+ 8548 et 144 panneaux solaires afin d’amener énergie et électricité à une exploitation agricole qui connaissait des coupures d'alimentation fréquentes. Désormais, la production agricole est plus abondante et contribue à nourrir environ 450 000 personnes.",
          "p7": "Enfin, Schneider Electric et GVE se sont associés pour fournir des solutions solaires à des populations dans des zones isolées, afin d’aider les écoles à faire reculer le paludisme."
        },
        "vid3": {
          "title": "Accès à l’énergie en Afrique de l’Ouest",
          "desc": "Apporter de l’électricité sûre et fiable aux communautés locales"
        },
        "txtMedia4": {
          "sectionTitle": "Faits marquants du programme Accès à l’énergie en 2017 : investissements à impact",
          "title": "Augmentation du fonds EAV de 1 à 90 millions de dollars",
          "p1": "En 2017, Energy Access Ventures (EAV) a augmenté la taille de son fonds de 1 à 90 millions de dollars et a stimulé la croissance économique en Afrique subsaharienne. Cette opération a été possible grâce à trois investissements. La Société néerlandaise de financement du développement (Financierings-Maatschappij voor Ontwikkelingslanden) a rejoint le fonds, et les commanditaires existants, Schneider Electric et CDC, ont augmenté leur participation.  ",
          "p2": "EAV investit dans des entreprises à travers le monde pour financer des technologies et modèles économiques innovants dans le domaine de l’électricité."
        },
        "quote1": {
          "text": "Chez FMO, nous sommes très heureux de participer à l'augmentation de capital du fonds EAV. Ce fonds offre une opportunité unique grâce à son équipe totalement opérationnelle, la connaissance parfaite du marché de l'énergie en Afrique subsaharienne et une solide expérience dans l'investissement en début de croissance. Notre participation est en adéquation avec l'agenda stratégique de FMO qui vise à promouvoir les énergies renouvelables, la croissance inclusive et l'innovation",
          "author": "Marieke Roestenberg",
          "title": "Senior Investment Officer, Private Equity, <br>FMO"
        },
        "txtMedia5": {
          "title": "Fonds Schneider Electric Energy Access",
          "p1": "Le fonds Schneider Electric Energy Access (SEEA) associe les collaborateurs de Schneider Electric et ses partenaires économiques, au niveau mondial, en leur proposant de prendre une part active dans la fourniture d'énergie aux populations défavorisées. Cette structure d’investissement solidaire imaginée par Schneider Electric en faveur d’un développement responsable répond à la nouvelle législation française sur l’épargne salariale.",
          "p2": "Fin 2017, 5 300 salariés en France ont montré leur intérêt dans le programme d’Accès à l’énergie en investissant pour 16,8 millions d’euros.",
          "p3": "Au 31 décembre 2017, SEEA gérait les montants suivants :",
          "li1": "3 000 000 euros de capital investi par Schneider Electric ;",
          "li2": "2 300 000 euros investis par Schneider Énergie Sicav Solidaire (dont 500 000 euros en capital), Sicav gérant l’épargne salariale solidaire des employés de Schneider Electric en France ;",
          "li3": "200 000 euros de capital investi par Phitrust Partenaires."
        },
        "quote2": {
          "text": "Chez Amped Innovation, nous développons des partenariats avec des entreprises sociales pour créer des produits très abordables, destinés aux besoins essentiels des communautés. Notre objectif est de réduire le coût des produits afin de permettre une adoption rapide de technologies éprouvées",
          "author": "Andi Kleissner",
          "title": "Co-fondatrice et COO,",
          "company": "Amped Innovation"
        },
        "txt11": {
          "h2": "Faits marquants du programme Accès à l’énergie en 2017 : Formation et Entrepreneuriat",
          "title": "Vietnam",
          "p1": "En partenariat avec Green ID et les associations féminines locales, Schneider Electric a formé et soutenu des femmes de communautés isolées pour qu’elles apportent l’énergie à des personnes défavorisées. Ces entrepreneuses ont vu leur revenu augmenter de 30 % et ont acquis un prestige social au sein de leur communauté. ",
          "p2": "Le centre de formation de Schneider Electric basé à Ho Chi Minh City a permis de former 650 jeunes défavorisés aux métiers de l’électricité résidentielle et industrielle, et 100 % d’entre eux ont décroché un emploi après leur diplôme. En 2018, nous espérons former 1 000 à 1 500 étudiants."
        },
        "txtMedia6": {
          "title": "Inde",
          "p1": "Nous avons ouvert le septième centre de formation aux métiers de l’énergie solaire d’Inde, au sein de l’université Sri Sri à Cuttack. Nous avons également mis sur pied un nouveau centre d’excellence au sein de l'école d’ingénieurs New Horizon, et élargi notre collaboration avec l’institut de technologie Kalinga et l’école d’ingénieurs CV Raman à Bhubaneswar pour répondre à la pénurie de compétences digitales dans les secteurs en mutation rapide. ",
          "p2": "En octobre 2017, nous avons inauguré le centre Pradhan Mantri Kaushal Kendra à New Delhi. Il s'agit du premier centre du pays exclusivement centré sur les qualifications exigées dans le cadre de l’initiative sur les villes intelligentes lancée par le gouvernement.",
          "p3": "Enfin, la Fondation Schneider Electric Inde et le programme de développement rural de Sri Sri ont ouvert un nouveau centre de formation pour électriciens à la prison centrale de Srinagar. Cette formation aidera les prisonniers à entamer leur nouvelle vie avec de précieuses compétences en électricité. Vingt-six détenus suivent actuellement le parcours de formation."
        },
        "quote3": {
          "text": "training and entrepreneurship QUOTE TBD",
          "author": "Author",
          "title": "Title",
          "company": "Company"
        },
        "txt12": {
          "title": "Pakistan",
          "p1": "Avec le soutien de la Fondation Schneider Electric, deux centres de formation en électricité ont ouvert à Lahore, deux autres à Gujranwala et un à Wazirabad. Au total, le nombre de centres en fonction devrait s'élever à 52 fin 2018.",
          "p2": "Les étudiants, 201 à l’heure actuelle, ont déjà commencé leur formation et les inscriptions devraient grimper à presque 6 000 lorsque tous les centres auront ouvert.",
          "p3": "Ces centres sont établis en partenariat avec l’autorité en charge de la formation professionnelle et de l’éducation technique, le conseil de la formation professionnelle du Punjab, et l’organisation Muslim Hands."
        },
        "txt13": {
          "title": "Cambodge",
          "p1": "Nous nous sommes associés à la Fondation Don Bosco pour ouvrir un centre de formation Don Bosco, qui formera plus de 300 jeunes défavorisés chaque année."
        },
        "txt14": {
          "title": "Tchad",
          "p1": "En partenariat avec l’Office national pour la promotion de l’emploi et l’Institut panafricain pour le développement, 77 jeunes déscolarisés ont été formés et ont intégré le marché de l’emploi local."
        },
        "txt15": {
          "title": "Colombie",
          "p1": "En partenariat avec les ministères colombiens du Travail et de l’Éducation nationale, Schneider Electric a animé des séminaires tout au long de l'année, formant ainsi 79 étudiants et 71 formateurs."
        },
        "txt16": {
          "title": "Côte d’Ivoire",
          "p1": "En collaboration avec l'IECD (Institut Européen de Coopération et de Développement), nous avons conduit la rénovation du département électricité de trois centres de formation. Dans le centre de Bonoua, trente étudiants ont suivi trois ans de formation et 20 étudiants une formation courte. "
        },
        "txt17": {
          "title": "Algérie",
          "p1": "Nous avons signé un accord avec le Ministère algérien de la Formation Professionnelle et de l'Éducation et avec le Ministère français de l'Éducation Nationale et de la Recherche en vue de créer un centre de formation franco-algérien (CEAFE,) dédié à la formation aux métiers de l'énergie. Situé à Rouiba, ce centre est spécialisé dans l'efficacité énergétique et la maintenance industrielle."
        },
        "txt18": {
          "title": "Sénégal",
          "p1": "En partenariat avec notre distributeur local, LCS Technodidac, nous avons organisé un événement avec des acteurs privés et publics du secteur de l'éducation. Il s'agissait de  présenter les challenges en matière de développement de programmes pédagogiques pratiques pour répondre aux besoins du marché. "
        },
        "txt19": {
          "title": "Afrique du Sud",
          "p1": "La première promotion du French South African Schneider Electric Education Center, centre de formation franco-sud-africain, compte autant de filles que de garçons. Pendant un an, les étudiants seront formés aux métiers du génie électrique sur les installations domestiques et industrielles."
        },
        "txt20": {
          "title": "Tanzanie",
          "p1": "Nous avons installé une centrale solaire de 30 kW et un laboratoire au centre de formation, et organisé des conférences sur le modèle de marché du micro-grid ainsi que sur l’impact de l’IdO sur les applications industrielles et photovoltaïques."
        },
        "txt21": {
          "title": "Togo",
          "p1": "Après huit mois de formation à l’Energy Generation Academy de Lomé, au Togo, les étudiants de ce programme de formation à l’entrepreneuriat dans le secteur de l’énergie soutenu par Schneider Electric ont présenté leur projet lors du premier concours Africa Energy Generation Prize. "
        },
        "statement1": {
          "title": "Nous aidons à fournir de l'électricité à 100 000 personnes pour alimenter l'irrigation, l'éclairage, la pisciculture et plus encore."
        }
      }
    }
  }
}
</i18n>
<template>
  <div id="">
    <Hero :loResSrc="require('@/assets/img/outreach/outreach-energy-hero-lores.jpg')" :hiResSrc="require('@/assets/img/outreach/outreach-energy-hero.jpg')" chapter="05" :section="$t('outreach.section')" :title="$t('outreach.title')" :grayBG="true" />

    <section class="m-t-120 m-b-120">
      <Txt class="m-b-80">
        <p v-html="$t('outreach.energy.txt1.p1')"></p>
        <p id="a2eprogram" v-html="$t('outreach.energy.txt1.p2')"></p>
      </Txt>

      <Txt>
        <h2 v-html="$t('outreach.energy.table1.title')"></h2>
        <p v-html="$t('outreach.energy.table1.p1')"></p>
        <p v-html="$t('outreach.energy.table1.p2')"></p>
      </Txt>
      <Table class="m-b-80" columns="3">
        <TableRow>
          <TableCell class="no-overlay contain-img img-row" :bgimg="require('@/assets/img/outreach/Mobiya01_product-photo.jpg')">
          </TableCell>
          <TableCell class="no-overlay contain-img img-row" :bgimg="require('@/assets/img/outreach/HOMAYA_PANNEAU_2_LAMPES_2_PORTABLE.jpg')">
          </TableCell>
          <TableCell class="no-overlay contain-img img-row" :bgimg="require('@/assets/img/outreach/Villaya-Microgrid_SAM_1992.jpg')">
          </TableCell>
        </TableRow>
      </Table>

      <Txt class="m-b-50">
        <h2 v-html="$t('outreach.energy.txt2.title')"></h2>
        <p v-html="$t('outreach.energy.txt2.p1')"></p>
        <p v-html="$t('outreach.energy.txt2.p2')"></p>
      </Txt>
      <Txt>
        <h2 v-html="$t('outreach.energy.txt3.title')"></h2>
        <p v-html="$t('outreach.energy.txt3.p1')"></p>
        <p v-html="$t('outreach.energy.txt3.p2')"></p>
      </Txt>
    </section>

    <section class="m-b-120 m-t-120 l-section-pad l-bg-white">
      <Txt class="m-b-0">
        <h2 id="a2eoffersandbusiness" v-html="$t('outreach.energy.txtMedia1.sectionTitle')"></h2>
      </Txt>

      <Txt class="m-b-15">
        <h3 class="m-b-15" v-html="$t('outreach.energy.txtMedia1.title')"></h3>
        <p v-html="$t('outreach.energy.txtMedia1.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Bangladesh_Mobiya_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-15">
        <h3 id="cambodia" class="m-b-15" v-html="$t('outreach.energy.txtMedia2.title')"></h3>
        <p v-html="$t('outreach.energy.txtMedia2.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Training_Cambodia_solar_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-15">
        <h3 id="cameroon" class="m-b-15" v-html="$t('outreach.energy.txtMedia3.title')"></h3>
        <p v-html="$t('outreach.energy.txtMedia3.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Cameroon_mobiya_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-15">
        <h3 id="congo" v-html="$t('outreach.energy.txt4.title')"></h3>
        <p v-html="$t('outreach.energy.txt4.p1')"></p>
      </Txt>

      <YouTubeVideo class="m-b-80" videoId="-ciu6QEEHE4" :title="$t('outreach.energy.vid1.title')" :description="$t('outreach.energy.vid1.desc')" :preview="require('@/assets/img/thumbnails/Proton.jpg')" />

      <Txt class="m-b-50">
        <h3 id="india" v-html="$t('outreach.energy.txt5.title')"></h3>
        <p v-html="$t('outreach.energy.txt5.p1')"></p>
      </Txt>

      <Txt class="m-b-50">
        <h3 id="kenya" v-html="$t('outreach.energy.txt6.title')"></h3>
        <p v-html="$t('outreach.energy.txt6.p1')"></p>
      </Txt>

      <YouTubeVideo class="m-b-80" videoId="lXcPsPEpQLA" :title="$t('outreach.energy.vid2.title')" :description="$t('outreach.energy.vid2.desc')" :preview="require('@/assets/img/thumbnails/Villaya-Microgrids.jpg')"/>

      <Txt class="m-b-15">
        <h3 id="myanmar" v-html="$t('outreach.energy.txt7.title')"></h3>
        <p v-html="$t('outreach.energy.txt7.p1')"></p>
      </Txt>

      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/Kenti-myanmar.jpg" />
      </ImageFull>

      <Txt>
        <h3 id="nepal" v-html="$t('outreach.energy.txt8.title')"></h3>
        <p v-html="$t('outreach.energy.txt8.p1')"></p>
      </Txt>
    </section>

    <section class="m-b-120 m-t-120">
      <Barometer>
        <h2 class="t-grey" slot="copy" v-html="$t('outreach.energy.barometer1.title')"/>
        <CTA slot="copy" href="/static/PSBarometer-Q42017_results.pdf" :buttonText="$t('outreach.energy.barometer1.cta')" target="_blank"/>
        <BarometerIndicator class="col-xs-18" color="#059941" :title="$t('outreach.energy.barometer1.card1.title')" :stat="$t('outreach.energy.barometer1.card1.stat')" :body="$t('outreach.energy.barometer1.card1.body')"/>
      </Barometer>
    </section>

    <section class="l-bg-white l-section-pad">
      <ImageFull class="m-b-50">
        <img :src="require('@/assets/img/outreach/Villaya-Agri-Business_InaugurationGrenoble.jpg')" alt="">
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="africa" v-html="$t('outreach.energy.txt9.title')"></h3>
        <p v-html="$t('outreach.energy.txt9.p1')"></p>
        <p id="villayaagri-business" v-html="$t('outreach.energy.txt9.p2')"></p>
        <p v-html="$t('outreach.energy.txt9.p3')"></p>
      </Txt>

      <Statement class="m-b-50">
        <h3 v-html="$t('outreach.energy.statement1.title')"></h3>
      </Statement>

      <Txt class="m-b-32">
        <p v-html="$t('outreach.energy.txt9.p4')"></p>
        <p v-html="$t('outreach.energy.txt9.p5')"></p>
        <p v-html="$t('outreach.energy.txt9.p6')"></p>
        <p v-html="$t('outreach.energy.txt9.p7')"></p>
      </Txt>

      <YouTubeVideo videoId="t_5vU2uvD7U" :title="$t('outreach.energy.vid3.title')" :description="$t('outreach.energy.vid3.desc')" :preview="require('@/assets/img/thumbnails/GVE-ScreenShot.jpg')" />
    </section>

    <section class="m-t-120">
      <div class="l-content">
        <div class="row">
          <div class="col-xs-14 col-xs-offset-2">
            <div class="m-b-50">
              <h2 id="a2eimpactinvesting" class="t-grey" v-html="$t('outreach.energy.txtMedia4.sectionTitle')"></h2>
            </div>
          </div>
        </div>
      </div>

      <TxtMedia class="m-b-50">
        <h3 class="m-b-15" slot="main-content" v-html="$t('outreach.energy.txtMedia4.title')"></h3>
        <p slot="main-content" v-html="$t('outreach.energy.txtMedia4.p1')"></p>
        <p slot="main-content" v-html="$t('outreach.energy.txtMedia4.p2')"></p>
        <img slot="sidebar" src="@/assets/img/outreach/Energy-Access-Ventures_logo.png" alt="">
      </TxtMedia>

      <Quote class="m-b-80" :name="$t('outreach.energy.quote1.author')" :title="$t('outreach.energy.quote1.title')" :photo="require('@/assets/img/outreach/Marieke-Roestenberg_FMO.jpg')">
        <div v-html="$t('outreach.energy.quote1.text')"></div>
      </Quote>

      <Txt class="m-b-50">
        <div>
          <h3 id="seeafund" class="m-b-15" v-html="$t('outreach.energy.txtMedia5.title')"></h3>
          <p v-html="$t('outreach.energy.txtMedia5.p1')"></p>
          <p v-html="$t('outreach.energy.txtMedia5.p2')"></p>
          <p v-html="$t('outreach.energy.txtMedia5.p3')"></p>
          <ul>
            <li v-html="$t('outreach.energy.txtMedia5.li1')"></li>
            <li v-html="$t('outreach.energy.txtMedia5.li2')"></li>
            <li v-html="$t('outreach.energy.txtMedia5.li3')"></li>
          </ul>
        </div>
      </Txt>

      <Quote :name="$t('outreach.energy.quote2.author')" :title="$t('outreach.energy.quote2.title')" :company="$t('outreach.energy.quote2.company')" :photo="require('@/assets/img/outreach/Andi_Kleissner_Amped.jpg')">
        <div v-html="$t('outreach.energy.quote2.text')"></div>
      </Quote>
    </section>

    <section class="l-bg-white l-section-pad m-b-120">
      <Txt class="m-b-15">
        <h2 id="a2etraining" v-html="$t('outreach.energy.txt11.h2')"></h2>
        <h3 v-html="$t('outreach.energy.txt11.title')"></h3>
        <p v-html="$t('outreach.energy.txt11.p1')"></p>
        <p v-html="$t('outreach.energy.txt11.p2')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_training_Vietnam_2017.jpg" />
      </ImageFull>

      <Txt class="m-b-15">
        <h3 id="india2" v-html="$t('outreach.energy.txtMedia6.title')"></h3>
        <p v-html="$t('outreach.energy.txtMedia6.p1')"></p>
        <p v-html="$t('outreach.energy.txtMedia6.p2')"></p>
        <p v-html="$t('outreach.energy.txtMedia6.p3')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_India_training_4K5A7194.jpg" />
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="pakistan" v-html="$t('outreach.energy.txt12.title')"></h3>
        <p v-html="$t('outreach.energy.txt12.p1')"></p>
        <p v-html="$t('outreach.energy.txt12.p2')"></p>
        <p v-html="$t('outreach.energy.txt12.p3')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Training_Pakistan_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-15">
        <h3 id="cambodia2" v-html="$t('outreach.energy.txt13.title')"></h3>
        <p v-html="$t('outreach.energy.txt13.p1')"></p>
      </Txt>

      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Cambodia_5150_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="chad" v-html="$t('outreach.energy.txt14.title')"></h3>
        <p v-html="$t('outreach.energy.txt14.p1')"></p>
      </Txt>

      <Txt class="m-b-32">
        <h3 id="columbia" v-html="$t('outreach.energy.txt15.title')"></h3>
        <p v-html="$t('outreach.energy.txt15.p1')"></p>
      </Txt>

      <Txt class="m-b-15">
        <h3 id="ivorycoast" v-html="$t('outreach.energy.txt16.title')"></h3>
        <p v-html="$t('outreach.energy.txt16.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_training_Ivory-Coast_2017.jpg" />
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="algeria" v-html="$t('outreach.energy.txt17.title')"></h3>
        <p v-html="$t('outreach.energy.txt17.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_Algeria_2017SSD.jpg" />
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="senegal" v-html="$t('outreach.energy.txt18.title')"></h3>
        <p v-html="$t('outreach.energy.txt18.p1')"></p>
      </Txt>

      <Txt class="m-b-15">
        <h3 id="southafrica" v-html="$t('outreach.energy.txt19.title')"></h3>
        <p v-html="$t('outreach.energy.txt19.p1')"></p>
      </Txt>
      <ImageFull class="m-b-80">
        <img src="@/assets/img/outreach/A2E_training_South-Africa2017.jpg" />
      </ImageFull>

      <Txt class="m-b-32">
        <h3 id="tanzania" v-html="$t('outreach.energy.txt20.title')"></h3>
        <p v-html="$t('outreach.energy.txt20.p1')"></p>
      </Txt>

      <Txt>
        <h3 id="togo" v-html="$t('outreach.energy.txt21.title')"></h3>
        <p v-html="$t('outreach.energy.txt21.p1')"></p>
      </Txt>
    </section>

   <div class="l-content">
      <FooterNav
        nextRoute="Outreach_SchneiderElectricFoundation"
        prevRoute="Outreach_Overview" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Txt from '@/components/Txt'
import References from '@/components/References'
import Reference from '@/components/Reference'
import YouTubeVideo from '@/components/YouTubeVideo'
import ImageFull from '@/components/ImageFull'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import TxtMedia from '@/components/TxtMedia'
import Barometer from '@/components/Barometer'
import BarometerIndicator from '@/components/BarometerIndicator'

export default {
  name: 'Outreach_AccessToEnergy',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Txt,
    References,
    Reference,
    YouTubeVideo,
    ImageFull,
    Table,
    TableRow,
    TableCell,
    TxtMedia,
    Barometer,
    BarometerIndicator
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/outreach/access-to-energy.vue